import * as XLSX from 'xlsx';
import { OptimizationResults, OptimizationResultsWithStats, StatisticalMetrics } from '../types';
import { HistoryEntry } from './historyStorage';
import { calculateCombinedWinner } from './combinedWinnerUtils';
import {
  AlgorithmKey,
  calculateAverageQTI,
  calculateQTIExceptions,
  calculateQTIForResults,
  calculateQTIFromEntries,
  QTITaskRow,
  QTIRow,
} from './qti';
import {
  RESULT_ALGORITHM_NAMES,
  ResultAlgorithmKey,
  getResultAlgorithmKeysForResults,
  getResultAlgorithmKeysForResultsList,
} from './algorithmRegistry';
import { resolveSflagaEnhancements } from './parameterUtils';

type ExportSummaryBasis = 'raw' | 'median';

function getAlgorithmsForResults(
  results: OptimizationResults | OptimizationResultsWithStats
): { algorithmKeys: ResultAlgorithmKey[]; algorithms: string[] } {
  const algorithmKeys = getResultAlgorithmKeysForResults(results);
  const algorithms = algorithmKeys.map((key) => RESULT_ALGORITHM_NAMES[key]);

  return { algorithmKeys, algorithms };
}

function getAlgorithmsForEntries(
  entries: HistoryEntry[]
): { algorithmKeys: ResultAlgorithmKey[]; algorithms: string[] } {
  const algorithmKeys = getResultAlgorithmKeysForResultsList(entries.map((entry) => entry.results));
  const algorithms = algorithmKeys.map((key) => RESULT_ALGORITHM_NAMES[key]);

  return { algorithmKeys, algorithms };
}

function getAlgorithmsForCompleteRunResults(
  completeRunResults: CompleteRunResult[]
): { algorithmKeys: ResultAlgorithmKey[]; algorithms: string[] } {
  const algorithmKeys = getResultAlgorithmKeysForResultsList(
    completeRunResults.map((entry) => entry.results)
  );
  const algorithms = algorithmKeys.map((key) => RESULT_ALGORITHM_NAMES[key]);

  return { algorithmKeys, algorithms };
}

function formatSheetValue(value: number): number | string {
  return Number.isFinite(value) ? value : 'N/A';
}

function getResolvedQuartiles(stats: StatisticalMetrics): { q1: number; q3: number; iqr: number; range: number } {
  const q1 = typeof stats.q1 === 'number' && Number.isFinite(stats.q1) ? stats.q1 : stats.median;
  const q3 = typeof stats.q3 === 'number' && Number.isFinite(stats.q3) ? stats.q3 : stats.median;

  return {
    q1,
    q3,
    iqr: q3 - q1,
    range: stats.max - stats.min,
  };
}

function resolveExportSummaryBasis(results: OptimizationResults | OptimizationResultsWithStats): ExportSummaryBasis {
  return 'statistics' in results && results.statistics ? 'median' : 'raw';
}

function getExportSummaryBasisLabel(
  results: OptimizationResults | OptimizationResultsWithStats
): string {
  return resolveExportSummaryBasis(results) === 'median'
    ? 'Median summary (statistics available)'
    : 'Raw single-run values';
}

function formatEnabledDisabled(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}

function getSflagaEnhancementSummaryLine(
  results: OptimizationResults | OptimizationResultsWithStats
): string {
  const enhancements = resolveSflagaEnhancements(results.configuration.sflagaEnhancements);

  return [
    `Guided Mutation=${formatEnabledDisabled(enhancements.guidedMutation)}`,
    `Elite Preservation=${formatEnabledDisabled(enhancements.elitePreservation)}`,
    `Similarity Diversification=${formatEnabledDisabled(enhancements.similarityAwareDiversification)}`,
    `Adaptive Immigrant Refresh=${formatEnabledDisabled(enhancements.adaptiveImmigrantRefresh)}`,
  ].join(', ');
}

function getExportMetricValue(
  results: OptimizationResults | OptimizationResultsWithStats,
  key: AlgorithmKey,
  metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
  summaryBasis: ExportSummaryBasis = resolveExportSummaryBasis(results)
): number {
  if (summaryBasis === 'median' && 'statistics' in results) {
    const stats = results.statistics?.[key];
    if (stats) {
      return stats[metric].median;
    }
  }

  const algorithmResult = results[key];
  if (!algorithmResult) {
    return NaN;
  }

  if (metric === 'executionTime') {
    return algorithmResult.executionTime ?? NaN;
  }
  if (metric === 'fitness') {
    return algorithmResult.fitness;
  }
  if (metric === 'energy') {
    return algorithmResult.energy;
  }
  if (metric === 'cost') {
    return algorithmResult.cost;
  }
  return algorithmResult.responseTime;
}

function createQTISheet(
  rows: QTIRow[],
  fitnessLabel: string = 'Fitness',
  summaryBasisLabel: string = 'Raw single-run values'
): XLSX.WorkSheet {
  const data: (string | number)[][] = [
    ['Derived Quality-Time Index (QTI)'],
    ['Formula', 'QTI = Fitness / (1 + normalized execution time)'],
    ['Summary Basis', summaryBasisLabel],
    ['Note', 'Execution time is min-max normalized across algorithms for this result set. QTI is a secondary reporting metric.'],
    [''],
    ['Algorithm', fitnessLabel, 'Execution Time (ms)', 'Execution Time Norm', 'QTI'],
  ];

  rows.forEach((row) => {
    data.push([
      row.algorithmName,
      formatSheetValue(row.fitness),
      formatSheetValue(row.executionTime),
      formatSheetValue(row.executionTimeNorm),
      formatSheetValue(row.qti),
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [
    { wch: 14 },
    { wch: 18 },
    { wch: 20 },
    { wch: 20 },
    { wch: 16 },
  ];

  return sheet;
}

/**
 * Helper function to create box plot sheet for any metric
 */
function createBoxPlotDataSheet(
  statistics: any,
  metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
  algorithms: string[],
  algorithmKeys: readonly string[]
) {
  const header: (string | number)[] = ['Algorithm', 'Min', 'Q1 (25%)', 'Median (50%)', 'Q3 (75%)', 'Max', 'Mean', 'Std Dev', 'IQR', 'Range'];
  const data: (string | number)[][] = [header];

  algorithmKeys.forEach((key, idx) => {
    const stats = statistics[key][metricKey];
    const { q1, q3, iqr, range } = getResolvedQuartiles(stats);
    
    data.push([
      algorithms[idx],
      stats.min,
      q1,
      stats.median,
      q3,
      stats.max,
      stats.mean,
      stats.stdDev,
      iqr,
      range,
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  sheet['!cols'] = [
    { wch: 12 }, // Algorithm
    { wch: 12 }, // Min
    { wch: 12 }, // Q1
    { wch: 12 }, // Median
    { wch: 12 }, // Q3
    { wch: 12 }, // Max
    { wch: 12 }, // Mean
    { wch: 12 }, // Std Dev
    { wch: 12 }, // IQR
    { wch: 12 }, // Range
  ];

  return sheet;
}

/**
 * Helper function to add box plot sheets to any workbook when statistics are available
 */
function addBoxPlotSheetsIfAvailable(
  workbook: XLSX.WorkBook,
  results: OptimizationResults | OptimizationResultsWithStats
): void {
  // Check if statistics are available
  const hasStatistics = 'statistics' in results && results.statistics !== undefined;
  if (!hasStatistics || !results.statistics) {
    return; // No statistics, skip adding box plot sheets
  }

  const { algorithms, algorithmKeys } = getAlgorithmsForResults(results);

  // Add box plot sheets for each metric
  const energyBoxPlot = createBoxPlotDataSheet(results.statistics, 'energy', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, energyBoxPlot, 'Energy Box Plot');

  const fitnessBoxPlot = createBoxPlotDataSheet(results.statistics, 'fitness', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, fitnessBoxPlot, 'Fitness Box Plot');

  const costBoxPlot = createBoxPlotDataSheet(results.statistics, 'cost', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, costBoxPlot, 'Cost Box Plot');

  const responseTimeBoxPlot = createBoxPlotDataSheet(results.statistics, 'responseTime', algorithms, algorithmKeys);
  XLSX.utils.book_append_sheet(workbook, responseTimeBoxPlot, 'Response Time Box Plot');
}

/**
 * Generates analysis report text from optimization results
 */
export function generateAnalysisText(results: OptimizationResults | OptimizationResultsWithStats): string {
  const { algorithms, algorithmKeys } = getAlgorithmsForResults(results);
  const summaryBasis = resolveExportSummaryBasis(results);
  const summaryBasisLabel = getExportSummaryBasisLabel(results);
  const includeSflaGaBaseline =
    results.configuration.includeSflaGaBaseline ?? Boolean(results.sflaGa);

  const lines: string[] = [];
  lines.push('='.repeat(80));
  lines.push('ANALYSIS RESULTS');
  lines.push('='.repeat(80));
  lines.push(
    `Configuration: Tasks=${results.configuration.taskCount}, Properties=${results.configuration.propertyCount}, Population=${results.configuration.initialPopulation}, Iterations=${results.configuration.iterations ?? 'N/A'}`
  );
  if (results.configuration.parameterPolicy) {
    lines.push(`Parameter Policy: ${results.configuration.parameterPolicy}`);
  }
  if (
    results.configuration.selectionRate !== undefined ||
    results.configuration.crossover !== undefined ||
    results.configuration.mutation !== undefined ||
    results.configuration.memeplexCount !== undefined
  ) {
    lines.push(
      `Shared Parameters: Selection=${results.configuration.selectionRate ?? 'N/A'}%, Crossover=${results.configuration.crossover}%` +
      `, Mutation=${results.configuration.mutation}%, Memeplex=${results.configuration.memeplexCount ?? 'N/A'}`
    );
  }
  lines.push(`SFLAGA Enhancements: ${getSflagaEnhancementSummaryLine(results)}`);
  if (includeSflaGaBaseline) {
    lines.push('SFLA-GA Baseline: Included');
  }
  lines.push(
    `QoS Variation: ${results.configuration.taskSpecificVariationEnabled === false ? 'Fixed Raw QWS Values' : 'Seeded Task-Specific Variation'}`
  );
  lines.push(`Summary Basis: ${summaryBasisLabel}`);
  if (results.configuration.seed !== undefined || results.configuration.runSeed !== undefined) {
    lines.push(`Seeds: Matrix=${results.configuration.seed ?? 'N/A'}, Run=${results.configuration.runSeed ?? 'N/A'}`);
  }
  if (results.configuration.fitnessMode) {
    lines.push(`Fitness Mode: ${results.configuration.fitnessMode === 'energy-aware' ? 'Energy-Aware (Composite)' : 'QoS-Only'}`);
    if (results.configuration.fitnessMode === 'energy-aware' && results.configuration.energyAwareWeights) {
      const w = results.configuration.energyAwareWeights;
      lines.push(`Energy-Aware Weights: QoS=${(w.fitness * 100).toFixed(0)}%, Energy=${(w.energy * 100).toFixed(0)}%`);
    }
  }
  lines.push('');

  // Fitness comparison
  const fitnessLabel = results.configuration.fitnessMode === 'energy-aware' 
    ? 'COMPOSITE FITNESS (Energy-Aware)' 
    : 'FITNESS (QoS-Only)';
  lines.push(`1. ${fitnessLabel} COMPARISON (Higher is Better)`);
  lines.push('-'.repeat(80));
  const fitnessData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    fitness: getExportMetricValue(results, key, 'fitness', summaryBasis),
  })).sort((a, b) => b.fitness - a.fitness);
  lines.push(`Winner: ${fitnessData[0].name} with Fitness = ${fitnessData[0].fitness.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  fitnessData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.fitness.toFixed(6)}`);
  });
  lines.push('');

  // Energy comparison
  lines.push('2. ENERGY CONSUMPTION COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const energyData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    energy: getExportMetricValue(results, key, 'energy', summaryBasis),
  })).sort((a, b) => a.energy - b.energy);
  lines.push(`Winner: ${energyData[0].name} with Energy = ${energyData[0].energy.toFixed(6)} Joules`);
  lines.push('');
  lines.push('Ranking:');
  energyData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.energy.toFixed(6)} Joules`);
  });
  lines.push('');

  // Response Time comparison
  lines.push('3. RESPONSE TIME COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const responseTimeData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    responseTime: getExportMetricValue(results, key, 'responseTime', summaryBasis),
  })).sort((a, b) => a.responseTime - b.responseTime);
  lines.push(`Winner: ${responseTimeData[0].name} with Response Time = ${responseTimeData[0].responseTime.toFixed(2)} ms`);
  lines.push('');
  lines.push('Ranking:');
  responseTimeData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.responseTime.toFixed(2)} ms`);
  });
  lines.push('');

  // Execution Cost comparison
  lines.push('4. EXECUTION COST COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const costData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    cost: getExportMetricValue(results, key, 'cost', summaryBasis),
  })).sort((a, b) => a.cost - b.cost);
  lines.push(`Winner: ${costData[0].name} with Cost = ${costData[0].cost.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  costData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.cost.toFixed(6)}`);
  });
  lines.push('');

  // Combined Winner (Energy-First Ranking)
  lines.push('5. COMBINED WINNER (Energy-First Ranking)');
  lines.push('-'.repeat(80));
  const combinedWinner = calculateCombinedWinner(results);
  if (combinedWinner) {
    if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
      lines.push('Perfect Winner(s) - #1 in BOTH Energy AND Fitness:');
      combinedWinner.perfectWinners.forEach(winner => {
        lines.push(`  ${winner.name.padEnd(10)} : Energy = ${winner.energy.toFixed(6)} J (#1), Fitness = ${winner.fitness.toFixed(6)} (#1)`);
      });
    } else if (combinedWinner.bestAlternative) {
      lines.push('Best Alternative (Lowest-energy candidate with strongest secondary score):');
      const alt = combinedWinner.bestAlternative;
      const energyStatus = alt.energy === combinedWinner.bestEnergy ? '#1 - Lowest Energy' : `Rank #${alt.energyRank}`;
      const fitnessStatus = alt.fitness === combinedWinner.bestFitness ? '#1 - Best Solution' : `Rank #${alt.fitnessRank}`;
      lines.push(`  ${alt.name.padEnd(10)} : Energy = ${alt.energy.toFixed(6)} J (${energyStatus}), Fitness = ${alt.fitness.toFixed(6)} (${fitnessStatus})`);
    }
    lines.push('');
    lines.push('Combined Ranking:');
    combinedWinner.rankedAlgorithms.forEach((alg, idx) => {
      const status = alg.isPerfectWinner ? ' [PERFECT WINNER]' : alg.isBestAlternative ? ' [BEST ALTERNATIVE]' : '';
      lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : Energy Rank #${alg.energyRank}, Fitness Rank #${alg.fitnessRank}${status}`);
    });
  } else {
    lines.push('No combined winner data available.');
  }
  lines.push('');

  return lines.join('\n');
}

/**
 * Exports optimization results to Excel file
 */
export function exportToExcel(
  results: OptimizationResults | OptimizationResultsWithStats,
  filename: string = 'optimization_results.xlsx'
): void {
  const workbook = XLSX.utils.book_new();
  const summaryBasis = resolveExportSummaryBasis(results);
  const summaryBasisLabel = getExportSummaryBasisLabel(results);
  const includeSflaGaBaseline =
    results.configuration.includeSflaGaBaseline ?? Boolean(results.sflaGa);
  const sflagaEnhancements = resolveSflagaEnhancements(results.configuration.sflagaEnhancements);

  // Sheet 1: Fitness Results
  const fitnessHeader = results.configuration.fitnessMode === 'energy-aware' 
    ? 'Composite Fitness (Energy-Aware)' 
    : 'Fitness (QoS-Only)';
  const { algorithmKeys } = getAlgorithmsForResults(results);
  const algorithmSummaryRows = algorithmKeys.map((key) => [
    RESULT_ALGORITHM_NAMES[key],
    getExportMetricValue(results, key, 'fitness', summaryBasis),
    getExportMetricValue(results, key, 'energy', summaryBasis),
    getExportMetricValue(results, key, 'cost', summaryBasis),
    getExportMetricValue(results, key, 'responseTime', summaryBasis),
  ]);
  const fitnessData = [
    ['Summary Basis', summaryBasisLabel, '', '', ''],
    ['Algorithm', fitnessHeader, 'Energy (Joules)', 'Cost', 'Response Time (ms)'],
    ...algorithmSummaryRows,
  ];
  const fitnessSheet = XLSX.utils.aoa_to_sheet(fitnessData);
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Results');

  // Sheet 2: Solutions
  const firstAlgorithmKey = algorithmKeys.find((key) => results[key] !== undefined);
  const firstSolutionLength = firstAlgorithmKey ? results[firstAlgorithmKey]!.solution.length : 0;
  const solutionsData: (string | number)[][] = [
    ['Algorithm', ...Array.from({ length: firstSolutionLength }, (_, i) => `Task ${i + 1}`)],
  ];
  
  const solutionRows = algorithmKeys.map((key) => ({
    name: RESULT_ALGORITHM_NAMES[key],
    solution: results[key]!.solution,
  }));

  solutionRows.forEach(({ name, solution }) => {
    solutionsData.push([name, ...solution]);
  });

  const solutionsSheet = XLSX.utils.aoa_to_sheet(solutionsData);
  XLSX.utils.book_append_sheet(workbook, solutionsSheet, 'Solutions');

  // Sheet 3: Configuration
  const configData = [
    ['Parameter', 'Value'],
    ['Summary Basis', summaryBasisLabel],
    ['Parameter Policy', results.configuration.parameterPolicy ?? 'N/A'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Iterations', results.configuration.iterations ?? 'N/A'],
    ['Selection Rate', results.configuration.selectionRate ?? 'N/A'],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
    ['Memeplex Count', results.configuration.memeplexCount ?? 'N/A'],
    ['Guided Mutation', formatEnabledDisabled(sflagaEnhancements.guidedMutation)],
    ['Elite Preservation', formatEnabledDisabled(sflagaEnhancements.elitePreservation)],
    ['Similarity Diversification', formatEnabledDisabled(sflagaEnhancements.similarityAwareDiversification)],
    ['Adaptive Immigrant Refresh', formatEnabledDisabled(sflagaEnhancements.adaptiveImmigrantRefresh)],
    ...(includeSflaGaBaseline ? [['SFLA-GA Baseline', 'Included']] : []),
    ['QoS Variation', results.configuration.taskSpecificVariationEnabled === false ? 'Fixed Raw QWS Values' : 'Seeded Task-Specific Variation'],
    ['Matrix Seed', results.configuration.seed ?? 'N/A'],
    ['Run Seed', results.configuration.runSeed ?? 'N/A'],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');

  // Sheet 4: Combined Winner
  const combinedWinner = calculateCombinedWinner(results);
  const combinedWinnerData: (string | number)[][] = [
    ['Combined Winner Analysis'],
    ['Summary Basis', summaryBasisLabel],
    [`Energy-first ranking: lowest ${summaryBasis === 'median' ? 'median energy' : 'energy'}, then composite score / fitness`],
    [''],
  ];
  
  if (combinedWinner) {
    if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
      combinedWinnerData.push(['Perfect Winner(s) - #1 in BOTH Energy AND Fitness:']);
      combinedWinner.perfectWinners.forEach(winner => {
        combinedWinnerData.push([
          winner.name,
          winner.fitness,
          winner.energy,
          'Perfect Winner'
        ]);
      });
    } else if (combinedWinner.bestAlternative) {
      combinedWinnerData.push(['Best Alternative (Lowest-energy candidate with strongest secondary score):']);
      const alt = combinedWinner.bestAlternative;
      combinedWinnerData.push([
        alt.name,
        alt.fitness,
        alt.energy,
        'Best Alternative'
      ]);
    }
    combinedWinnerData.push(['']);
    combinedWinnerData.push(['Algorithm', 'Energy', 'Energy Rank', 'Fitness', 'Fitness Rank', 'Status']);
    combinedWinner.rankedAlgorithms.forEach((alg) => {
      let status = '';
      if (alg.isPerfectWinner) {
        status = 'Perfect Winner';
      } else if (alg.isBestAlternative) {
        status = 'Best Alternative';
      } else {
        status = '-';
      }
      combinedWinnerData.push([
        alg.name,
        alg.energy,
        alg.energyRank,
        alg.fitness,
        alg.fitnessRank,
        status
      ]);
    });
  } else {
    combinedWinnerData.push(['No combined winner data available.']);
  }
  
  const combinedWinnerSheet = XLSX.utils.aoa_to_sheet(combinedWinnerData);
  XLSX.utils.book_append_sheet(workbook, combinedWinnerSheet, 'Combined Winner');

  const qtiSheet = createQTISheet(
    calculateQTIForResults(results, summaryBasis),
    summaryBasis === 'median' ? `${fitnessHeader} (Median)` : fitnessHeader,
    summaryBasisLabel
  );
  XLSX.utils.book_append_sheet(workbook, qtiSheet, 'QTI (Derived)');

  // Add box plot sheets if statistics are available
  addBoxPlotSheetsIfAvailable(workbook, results);

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports analysis report to text file
 */
export function exportAnalysisReport(analysisText: string, filename: string = 'analysis_report.txt'): void {
  const blob = new Blob([analysisText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports statistics results to Excel file
 */
export function exportStatisticsToExcel(results: OptimizationResultsWithStats, filename: string = 'statistics_results.xlsx'): void {
  if (!results.statistics) {
    throw new Error('No statistics available to export');
  }

  const workbook = XLSX.utils.book_new();
  const { algorithms, algorithmKeys } = getAlgorithmsForResults(results);
  const includeSflaGaBaseline =
    results.configuration.includeSflaGaBaseline ?? Boolean(results.sflaGa);
  const sflagaEnhancements = resolveSflagaEnhancements(results.configuration.sflagaEnhancements);

  // Helper function to create statistics sheet for a metric
  const createStatisticsSheet = (metricName: string, metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime') => {
    const header: (string | number)[] = ['Algorithm', 'Mean', 'Std Dev', 'Min', 'Max', 'Median'];
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const algorithmStats = results.statistics?.[key];
      if (!algorithmStats) {
        return;
      }
      const stats = algorithmStats[metricKey];
      data.push([
        algorithms[idx],
        stats.mean,
        stats.stdDev,
        stats.min,
        stats.max,
        stats.median,
      ]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 15 }, // Mean
      { wch: 15 }, // Std Dev
      { wch: 15 }, // Min
      { wch: 15 }, // Max
      { wch: 15 }, // Median
    ];

    return sheet;
  };

  // Fitness Statistics Sheet
  const fitnessSheet = createStatisticsSheet('Fitness', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Statistics');

  // Energy Statistics Sheet
  const energySheet = createStatisticsSheet('Energy', 'energy');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Statistics');

  // Cost Statistics Sheet
  const costSheet = createStatisticsSheet('Cost', 'cost');
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Statistics');

  // Response Time Statistics Sheet
  const responseTimeSheet = createStatisticsSheet('Response Time', 'responseTime');
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Statistics');

  // Execution Time Statistics Sheet
  const executionTimeSheet = createStatisticsSheet('Execution Time', 'executionTime');
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time Statistics');

  const qtiSummarySheet = createQTISheet(
    calculateQTIForResults(results, 'median'),
    'Fitness Median',
    'Median summary (statistics export)'
  );
  XLSX.utils.book_append_sheet(workbook, qtiSummarySheet, 'QTI Summary');

  // ==================== BOX PLOT STATISTICS SHEET ====================
  // Add comprehensive box plot data including quartiles for all metrics
  const createBoxPlotSheet = (metricName: string, metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime') => {
    const header: (string | number)[] = ['Algorithm', 'Min', 'Q1 (25%)', 'Median (50%)', 'Q3 (75%)', 'Max', 'Mean', 'Std Dev', 'IQR', 'Range'];
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const algorithmStats = results.statistics?.[key];
      if (!algorithmStats) {
        return;
      }
      const stats = algorithmStats[metricKey];
      const { q1, q3, iqr, range } = getResolvedQuartiles(stats);
      
      data.push([
        algorithms[idx],
        stats.min,
        q1,
        stats.median,
        q3,
        stats.max,
        stats.mean,
        stats.stdDev,
        iqr,
        range,
      ]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 12 }, // Min
      { wch: 12 }, // Q1
      { wch: 12 }, // Median
      { wch: 12 }, // Q3
      { wch: 12 }, // Max
      { wch: 12 }, // Mean
      { wch: 12 }, // Std Dev
      { wch: 12 }, // IQR
      { wch: 12 }, // Range
    ];

    return sheet;
  };

  // Box Plot Data for Energy (Primary Focus)
  const energyBoxPlotSheet = createBoxPlotSheet('Energy Box Plot', 'energy');
  XLSX.utils.book_append_sheet(workbook, energyBoxPlotSheet, 'Energy Box Plot');

  // Box Plot Data for Fitness
  const fitnessBoxPlotSheet = createBoxPlotSheet('Fitness Box Plot', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessBoxPlotSheet, 'Fitness Box Plot');

  // Box Plot Data for Cost
  const costBoxPlotSheet = createBoxPlotSheet('Cost Box Plot', 'cost');
  XLSX.utils.book_append_sheet(workbook, costBoxPlotSheet, 'Cost Box Plot');

  // Box Plot Data for Response Time
  const responseTimeBoxPlotSheet = createBoxPlotSheet('Response Time Box Plot', 'responseTime');
  XLSX.utils.book_append_sheet(workbook, responseTimeBoxPlotSheet, 'Response Time Box Plot');

  // Composite Score Statistics Sheet
  if (results.compositeScores) {
    const compositeHeader: (string | number)[] = ['Algorithm', 'Median Score', 'Mean Score', 'Std Dev'];
    const compositeData: (string | number)[][] = [compositeHeader];

    algorithmKeys.forEach((key, idx) => {
      const score = results.compositeScores![key];
      if (score) {
        compositeData.push([
          algorithms[idx],
          score.median,
          score.mean,
          score.stdDev,
        ]);
      }
    });

    const compositeSheet = XLSX.utils.aoa_to_sheet(compositeData);
    compositeSheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 15 }, // Median Score
      { wch: 15 }, // Mean Score
      { wch: 15 }, // Std Dev
    ];
    XLSX.utils.book_append_sheet(workbook, compositeSheet, 'Composite Score Statistics');
  }

  // Pareto Analysis Sheet
  if (results.paretoAnalysis) {
    const paretoHeader: (string | number)[] = ['Algorithm', 'Pareto-Optimal Count', 'Percentage Contribution'];
    const paretoData: (string | number)[][] = [paretoHeader];

    results.paretoAnalysis.algorithmContributions.forEach((contrib) => {
      paretoData.push([
        contrib.algorithmName,
        contrib.paretoOptimalCount,
        contrib.percentageContribution,
      ]);
    });

    const paretoSheet = XLSX.utils.aoa_to_sheet(paretoData);
    paretoSheet['!cols'] = [
      { wch: 15 }, // Algorithm
      { wch: 20 }, // Pareto-Optimal Count
      { wch: 20 }, // Percentage Contribution
    ];
    XLSX.utils.book_append_sheet(workbook, paretoSheet, 'Pareto Analysis');

    // Pareto Front Details Sheet
    // Pareto dominance is based on: energy (min), responseTime (min), cost (min)
    const paretoFrontHeader: (string | number)[] = ['Algorithm', 'Run ID', 'Energy', 'Response Time', 'Cost', 'Fitness (ref)'];
    const paretoFrontData: (string | number)[][] = [paretoFrontHeader];

    results.paretoAnalysis.globalParetoFront.forEach((solution) => {
      paretoFrontData.push([
        solution.algorithmName,
        solution.runId,
        solution.energy,
        solution.responseTime,
        solution.cost,
        solution.fitness ?? 'N/A', // Optional: kept for reference but not used in dominance
      ]);
    });

    const paretoFrontSheet = XLSX.utils.aoa_to_sheet(paretoFrontData);
    paretoFrontSheet['!cols'] = [
      { wch: 15 }, // Algorithm
      { wch: 10 }, // Run ID
      { wch: 15 }, // Energy
      { wch: 18 }, // Response Time
      { wch: 15 }, // Cost
      { wch: 15 }, // Fitness (ref)
    ];
    XLSX.utils.book_append_sheet(workbook, paretoFrontSheet, 'Pareto Front Details');
  }

  // Summary Sheet
  const summaryData = [
    ['Summary Information'],
    ['Number of Runs', results.runNumber || 1],
    ['Timestamp', results.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'],
    ['Parameter Policy', results.configuration.parameterPolicy ?? 'N/A'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Iterations', results.configuration.iterations ?? 'N/A'],
    ['Selection Rate', results.configuration.selectionRate ?? 'N/A'],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
    ['Memeplex Count', results.configuration.memeplexCount ?? 'N/A'],
    ['Guided Mutation', formatEnabledDisabled(sflagaEnhancements.guidedMutation)],
    ['Elite Preservation', formatEnabledDisabled(sflagaEnhancements.elitePreservation)],
    ['Similarity Diversification', formatEnabledDisabled(sflagaEnhancements.similarityAwareDiversification)],
    ['Adaptive Immigrant Refresh', formatEnabledDisabled(sflagaEnhancements.adaptiveImmigrantRefresh)],
    ...(includeSflaGaBaseline ? [['SFLA-GA Baseline', 'Included']] : []),
    ['QoS Variation', results.configuration.taskSpecificVariationEnabled === false ? 'Fixed Raw QWS Values' : 'Seeded Task-Specific Variation'],
    ['Matrix Seed', results.configuration.seed ?? 'N/A'],
    ['Run Seed', results.configuration.runSeed ?? 'N/A'],
    [''],
    ['Sheet Guide'],
    ['1. Fitness Statistics', 'Mean, StdDev, Min, Max, Median for Fitness'],
    ['2. Energy Statistics', 'Mean, StdDev, Min, Max, Median for Energy'],
    ['3. Cost Statistics', 'Mean, StdDev, Min, Max, Median for Cost'],
    ['4. Response Time Statistics', 'Mean, StdDev, Min, Max, Median for Response Time'],
    ['5. Execution Time Statistics', 'Mean, StdDev, Min, Max, Median for Execution Time'],
    ['6. QTI Summary', 'Derived QTI using median fitness and normalized median execution time'],
    ['7. Energy Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['8. Fitness Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['9. Cost Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ['10. Response Time Box Plot', 'Box plot data with quartiles (Q1, Median, Q3, IQR)'],
    ...(results.compositeScores ? [['11. Composite Score Statistics', 'Weighted composite scores']] : []),
    ...(results.paretoAnalysis ? [
      ['12. Pareto Analysis', 'Pareto-optimal solution contributions'],
      ['13. Pareto Front Details', 'Individual Pareto-optimal solutions'],
    ] : []),
    [''],
    ['Box Plot Data Explanation'],
    ['Q1 (25%)', 'First Quartile - 25% of values fall below this'],
    ['Median (50%)', 'Second Quartile - middle value (primary comparison metric)'],
    ['Q3 (75%)', 'Third Quartile - 75% of values fall below this'],
    ['IQR', 'Interquartile Range (Q3 - Q1) - measures consistency'],
    ['Range', 'Total spread (Max - Min)'],
    [''],
    ['Interpretation Guide'],
    ['Energy', 'Lower median = Better efficiency, Smaller IQR = More consistent'],
    ['Fitness', 'Higher median = Better quality, Smaller IQR = More consistent'],
    ['Cost', 'Lower median = Better cost, Smaller IQR = More consistent'],
    ['Response Time', 'Lower median = Better performance, Smaller IQR = More consistent'],
    [''],
    ['Note: Statistics use MEDIAN as primary comparison metric'],
    ['Composite Score Weights: Fitness (50%), Energy (30%), Cost (20%)'],
    ['Summary Basis', 'Median summary (statistics export)'],
    ['QTI Formula', 'QTI = Fitness / (1 + normalized execution time)'],
    ...(results.paretoAnalysis ? [
      [''],
      ['Pareto Analysis Summary'],
      ['Total Solutions', results.paretoAnalysis.totalSolutions],
      ['Pareto-Optimal Solutions', results.paretoAnalysis.paretoOptimalSolutions],
      ['Pareto Efficiency', `${((results.paretoAnalysis.paretoOptimalSolutions / results.paretoAnalysis.totalSolutions) * 100).toFixed(2)}%`],
    ] : []),
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [
    { wch: 30 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports comparison results to Excel file
 */
export function exportComparisonToExcel(entries: HistoryEntry[], filename: string = 'comparison_results.xlsx'): void {
  if (entries.length === 0) {
    throw new Error('No entries to export');
  }

  const workbook = XLSX.utils.book_new();
  const { algorithms, algorithmKeys } = getAlgorithmsForEntries(entries);
  const hasSflaGaBaseline = entries.some(
    (entry) => entry.results.configuration.includeSflaGaBaseline ?? Boolean(entry.results.sflaGa)
  );

  // Helper function to create comparison sheet for a metric
  const createComparisonSheet = (
    metricName: string,
    getValue: (result: OptimizationResults, key: typeof algorithmKeys[number]) => number,
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Algorithm'];
    entries.forEach((entry, idx) => {
      header.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
    });
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const row: (string | number)[] = [algorithms[idx]];
      entries.forEach((entry) => {
        row.push(formatSheetValue(getValue(entry.results, key)));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      ...entries.map(() => ({ wch: 25 })), // Each run column
    ];

    return sheet;
  };

  // Fitness Comparison Sheet
  const fitnessSheet = createComparisonSheet(
    'Fitness',
    (result, key) => result[key]?.fitness ?? NaN
  );
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Comparison');

  // Energy Comparison Sheet
  const energySheet = createComparisonSheet(
    'Energy',
    (result, key) => result[key]?.energy ?? NaN,
    'J'
  );
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Comparison');

  // Cost Comparison Sheet
  const costSheet = createComparisonSheet(
    'Cost',
    (result, key) => result[key]?.cost ?? NaN
  );
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Comparison');

  // Response Time Comparison Sheet
  const responseTimeSheet = createComparisonSheet(
    'Response Time',
    (result, key) => result[key]?.responseTime ?? NaN,
    'ms'
  );
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Comparison');

  // Configuration Comparison Sheet
  const configHeader: (string | number)[] = ['Parameter'];
  entries.forEach((entry, idx) => {
    configHeader.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
  });
  const configData: (string | number)[][] = [
    configHeader,
    ['Parameter Policy', ...entries.map(e => e.results.configuration.parameterPolicy ?? 'N/A')],
    ['Task Count', ...entries.map(e => e.results.configuration.taskCount)],
    ['Property Count', ...entries.map(e => e.results.configuration.propertyCount)],
    ['Initial Population', ...entries.map(e => e.results.configuration.initialPopulation)],
    ['Iterations', ...entries.map(e => e.results.configuration.iterations ?? 'N/A')],
    ['Selection Rate', ...entries.map(e => e.results.configuration.selectionRate ?? 'N/A')],
    ['Crossover Rate', ...entries.map(e => e.results.configuration.crossover)],
    ['Mutation Rate', ...entries.map(e => e.results.configuration.mutation)],
    ['Memeplex Count', ...entries.map(e => e.results.configuration.memeplexCount ?? 'N/A')],
    ['Guided Mutation', ...entries.map(e => formatEnabledDisabled(resolveSflagaEnhancements(e.results.configuration.sflagaEnhancements).guidedMutation))],
    ['Elite Preservation', ...entries.map(e => formatEnabledDisabled(resolveSflagaEnhancements(e.results.configuration.sflagaEnhancements).elitePreservation))],
    ['Similarity Diversification', ...entries.map(e => formatEnabledDisabled(resolveSflagaEnhancements(e.results.configuration.sflagaEnhancements).similarityAwareDiversification))],
    ['Adaptive Immigrant Refresh', ...entries.map(e => formatEnabledDisabled(resolveSflagaEnhancements(e.results.configuration.sflagaEnhancements).adaptiveImmigrantRefresh))],
    ...(hasSflaGaBaseline ? [[
      'SFLA-GA Baseline',
      ...entries.map(e => (e.results.configuration.includeSflaGaBaseline ?? Boolean(e.results.sflaGa)) ? 'Included' : 'Excluded'),
    ]] : []),
    ['QoS Variation', ...entries.map(e => e.results.configuration.taskSpecificVariationEnabled === false ? 'Fixed Raw QWS Values' : 'Seeded Task-Specific Variation')],
    ['Matrix Seed', ...entries.map(e => e.results.configuration.seed ?? 'N/A')],
    ['Run Seed', ...entries.map(e => e.results.configuration.runSeed ?? 'N/A')],
    ['Number of Runs', ...entries.map(e => {
      const results = e.results as OptimizationResultsWithStats;
      return results.runNumber || 1;
    })],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration Comparison');

  // Add box plot sheets if any entry has statistics
  const entryWithStats = entries.find(e => 'statistics' in e.results && e.results.statistics !== undefined);
  if (entryWithStats) {
    addBoxPlotSheetsIfAvailable(workbook, entryWithStats.results);
  }

  // Write file
  XLSX.writeFile(workbook, filename);
}

export interface CompleteRunResult {
  taskCount: number;
  results: OptimizationResults | OptimizationResultsWithStats;
  timestamp: string;
}

function createCompleteRunBoxPlotSheet(
  completeRunResults: CompleteRunResult[],
  algorithms: string[],
  algorithmKeys: readonly ResultAlgorithmKey[],
  metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime'
): XLSX.WorkSheet | null {
  const header: (string | number)[] = [
    'Task Count',
    'Algorithm',
    'Min',
    'Q1 (25%)',
    'Median (50%)',
    'Q3 (75%)',
    'Max',
    'Mean',
    'Std Dev',
    'IQR',
    'Range',
  ];
  const data: (string | number)[][] = [header];

  completeRunResults.forEach((entry) => {
    if (!('statistics' in entry.results) || !entry.results.statistics) {
      return;
    }
    const statistics = entry.results.statistics;

    algorithmKeys.forEach((key, idx) => {
      const algorithmStats = statistics[key];
      if (!algorithmStats) {
        return;
      }

      const stats = algorithmStats[metricKey];
      const { q1, q3, iqr, range } = getResolvedQuartiles(stats);

      data.push([
        entry.taskCount,
        algorithms[idx],
        stats.min,
        q1,
        stats.median,
        q3,
        stats.max,
        stats.mean,
        stats.stdDev,
        iqr,
        range,
      ]);
    });
  });

  if (data.length === 1) {
    return null;
  }

  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  return sheet;
}

/**
 * Exports complete run results to Excel file with comprehensive findings
 */
export function exportCompleteRunToExcel(
  completeRunResults: CompleteRunResult[],
  filename: string = 'complete_run_results.xlsx'
): void {
  if (completeRunResults.length === 0) {
    throw new Error('No results to export');
  }

  const workbook = XLSX.utils.book_new();
  const { algorithms, algorithmKeys } = getAlgorithmsForCompleteRunResults(completeRunResults);
  const includeSflaGaBaseline = completeRunResults.some(
    (entry) => entry.results.configuration.includeSflaGaBaseline ?? Boolean(entry.results.sflaGa)
  );
  const completeRunSummaryBasisLabel = 'Median summary where statistics exist; raw single-run values otherwise';
  const statisticalRunCounts = Array.from(new Set(
    completeRunResults
      .map((entry) => ('runNumber' in entry.results ? (entry.results as OptimizationResultsWithStats).runNumber : undefined))
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0)
  ));
  const statisticalRunsPerConfiguration =
    statisticalRunCounts.length === 1
      ? statisticalRunCounts[0]
      : statisticalRunCounts.length > 1
        ? statisticalRunCounts.join(', ')
        : 1;

  // Helper function to get metric value using the export summary basis rule
  const getMetricValue = (
    result: OptimizationResults | OptimizationResultsWithStats,
    key: typeof algorithmKeys[number],
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime'
  ): number => {
    return getExportMetricValue(result, key, metric);
  };

  // Helper function to create a metric sheet across all task counts
  const createMetricSheet = (
    metricName: string,
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Task Count', ...algorithms];
    const data: (string | number)[][] = [header];

    completeRunResults.forEach((entry) => {
      const row: (string | number)[] = [entry.taskCount];
      algorithmKeys.forEach((key) => {
        row.push(getMetricValue(entry.results, key, metric));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Task Count
      ...algorithms.map(() => ({ wch: 15 })), // Algorithm columns
    ];

    return sheet;
  };

  // Helper function to create rankings sheet for a metric
  const createRankingsSheet = (
    metricName: string,
    metric: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
    higherIsBetter: boolean = true
  ) => {
    const header: (string | number)[] = ['Task Count', ...algorithms];
    const data: (string | number)[][] = [header];

    completeRunResults.forEach((entry) => {
      const row: (string | number)[] = [entry.taskCount];
      const values = algorithmKeys.map(key => ({
        key,
        value: getMetricValue(entry.results, key, metric)
      }));
      
      // Sort by value (descending for higher is better, ascending for lower is better)
      const sorted = [...values].sort((a, b) => 
        higherIsBetter ? b.value - a.value : a.value - b.value
      );
      
      // Create ranking array
      algorithmKeys.forEach(key => {
        const rank = sorted.findIndex(v => v.key === key) + 1;
        row.push(rank);
      });
      
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    sheet['!cols'] = [
      { wch: 12 },
      ...algorithms.map(() => ({ wch: 12 })),
    ];
    return sheet;
  };

  const qtiTaskRows: QTITaskRow[] = completeRunResults.map((entry) => ({
    taskCount: entry.taskCount,
    entries: calculateQTIFromEntries(
      algorithmKeys.map((key, idx) => ({
        algorithmKey: key,
        algorithmName: algorithms[idx],
        fitness: getMetricValue(entry.results, key, 'fitness'),
        executionTime: getMetricValue(entry.results, key, 'executionTime'),
      }))
    ),
  }));
  const averageQTIRows = calculateAverageQTI(qtiTaskRows);
  const qtiExceptions = calculateQTIExceptions(qtiTaskRows, 'sflaga');

  // ==================== EXISTING METRIC SHEETS ====================
  
  // Fitness Sheet
  const fitnessSheet = createMetricSheet('Fitness', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness');

  // Energy Sheet
  const energySheet = createMetricSheet('Energy', 'energy', 'J');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy');

  // Cost Sheet
  const costSheet = createMetricSheet('Cost', 'cost');
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost');

  // Response Time Sheet
  const responseTimeSheet = createMetricSheet('Response Time', 'responseTime', 'ms');
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time');

  // Execution Time Sheet
  const executionTimeSheet = createMetricSheet('Execution Time', 'executionTime', 'ms');
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time');

  // QTI Sheet
  const qtiSheetData: (string | number)[][] = [
    ['Derived Quality-Time Index (QTI)'],
    ['Formula', 'QTI = Fitness / (1 + normalized execution time)'],
    ['Summary Basis', completeRunSummaryBasisLabel],
    ['Note', 'Execution time is normalized separately for each task count across the compared algorithms.'],
    [''],
    ['Task Count', ...algorithms],
  ];
  qtiTaskRows.forEach((taskRow) => {
    qtiSheetData.push([
      taskRow.taskCount,
      ...algorithmKeys.map((key) => {
        const entry = taskRow.entries.find((row) => row.algorithmKey === key);
        return formatSheetValue(entry?.qti ?? NaN);
      }),
    ]);
  });
  const qtiSheet = XLSX.utils.aoa_to_sheet(qtiSheetData);
  qtiSheet['!cols'] = [
    { wch: 14 },
    ...algorithms.map(() => ({ wch: 16 })),
  ];
  XLSX.utils.book_append_sheet(workbook, qtiSheet, 'QTI');

  // ==================== RANKINGS SHEETS ====================
  
  // Fitness Rankings
  const fitnessRankingsSheet = createRankingsSheet('Fitness Rankings', 'fitness', true);
  XLSX.utils.book_append_sheet(workbook, fitnessRankingsSheet, 'Fitness Rankings');

  // Energy Rankings (lower is better)
  const energyRankingsSheet = createRankingsSheet('Energy Rankings', 'energy', false);
  XLSX.utils.book_append_sheet(workbook, energyRankingsSheet, 'Energy Rankings');

  // Cost Rankings (lower is better)
  const costRankingsSheet = createRankingsSheet('Cost Rankings', 'cost', false);
  XLSX.utils.book_append_sheet(workbook, costRankingsSheet, 'Cost Rankings');

  // Response Time Rankings (lower is better)
  const responseTimeRankingsSheet = createRankingsSheet('Response Time Rankings', 'responseTime', false);
  XLSX.utils.book_append_sheet(workbook, responseTimeRankingsSheet, 'Response Rankings');

  // ==================== DETAILED RESULTS SHEET ====================
  // All metrics for all algorithms at all task counts in one comprehensive table
  const detailedResultsHeader: (string | number)[] = [
    'Task Count', 'Algorithm', 'Fitness', 'Energy (J)', 'Cost', 'Response Time (ms)', 'Execution Time (ms)', 'QTI (Derived)'
  ];
  const detailedResultsData: (string | number)[][] = [detailedResultsHeader];

  completeRunResults.forEach((entry) => {
    const qtiEntries = qtiTaskRows.find((taskRow) => taskRow.taskCount === entry.taskCount)?.entries ?? [];
    algorithmKeys.forEach((key, idx) => {
      const qtiEntry = qtiEntries.find((row) => row.algorithmKey === key);
      detailedResultsData.push([
        entry.taskCount,
        algorithms[idx],
        getMetricValue(entry.results, key, 'fitness'),
        getMetricValue(entry.results, key, 'energy'),
        getMetricValue(entry.results, key, 'cost'),
        getMetricValue(entry.results, key, 'responseTime'),
        getMetricValue(entry.results, key, 'executionTime'),
        formatSheetValue(qtiEntry?.qti ?? NaN),
      ]);
    });
  });

  const detailedResultsSheet = XLSX.utils.aoa_to_sheet(detailedResultsData);
  detailedResultsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    { wch: 15 }, // Fitness
    { wch: 15 }, // Energy
    { wch: 15 }, // Cost
    { wch: 18 }, // Response Time
    { wch: 18 }, // Execution Time
    { wch: 14 }, // QTI
  ];
  XLSX.utils.book_append_sheet(workbook, detailedResultsSheet, 'Detailed Results');

  // ==================== SOLUTIONS SHEET ====================
  // All solutions for all algorithms at all task counts
  const maxSolutionLength = Math.max(...completeRunResults.map(r => r.results.ga.solution.length));
  const solutionsHeader: (string | number)[] = ['Task Count', 'Algorithm', ...Array.from({ length: maxSolutionLength }, (_, i) => `Task ${i + 1}`)];
  const solutionsData: (string | number)[][] = [solutionsHeader];

  completeRunResults.forEach((entry) => {
    algorithmKeys.forEach((key, idx) => {
      const solution = entry.results[key]?.solution ?? [];
      const row: (string | number)[] = [entry.taskCount, algorithms[idx]];
      // Pad solution array if needed
      for (let i = 0; i < maxSolutionLength; i++) {
        row.push(i < solution.length ? solution[i] : '');
      }
      solutionsData.push(row);
    });
  });

  const solutionsSheet = XLSX.utils.aoa_to_sheet(solutionsData);
  solutionsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    ...Array(maxSolutionLength).fill({ wch: 8 }), // Solution columns
  ];
  XLSX.utils.book_append_sheet(workbook, solutionsSheet, 'Solutions');

  // ==================== CONFIGURATION SHEET ====================
  const configHeader: (string | number)[] = [
    'Task Count', 'Timestamp', 'Task Count', 'Property Count', 'Initial Population', 'Crossover Rate', 'Mutation Rate',
    'Guided Mutation', 'Elite Preservation', 'Similarity Diversification', 'Adaptive Immigrant Refresh',
    ...(includeSflaGaBaseline ? ['SFLA-GA Baseline'] : []),
  ];
  const configData: (string | number)[][] = [configHeader];

  completeRunResults.forEach((entry) => {
    const config = entry.results.configuration;
    configData.push([
      entry.taskCount,
      new Date(entry.timestamp).toLocaleString(),
      config.taskCount,
      config.propertyCount,
      config.initialPopulation,
      config.crossover,
      config.mutation,
      formatEnabledDisabled(resolveSflagaEnhancements(config.sflagaEnhancements).guidedMutation),
      formatEnabledDisabled(resolveSflagaEnhancements(config.sflagaEnhancements).elitePreservation),
      formatEnabledDisabled(resolveSflagaEnhancements(config.sflagaEnhancements).similarityAwareDiversification),
      formatEnabledDisabled(resolveSflagaEnhancements(config.sflagaEnhancements).adaptiveImmigrantRefresh),
      ...(
        includeSflaGaBaseline
          ? [((config.includeSflaGaBaseline ?? Boolean(entry.results.sflaGa)) ? 'Included' : 'Excluded')]
          : []
      ),
    ]);
  });

  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  configSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 22 }, // Timestamp
    { wch: 12 }, // Task Count (config)
    { wch: 15 }, // Property Count
    { wch: 18 }, // Initial Population
    { wch: 15 }, // Crossover Rate
    { wch: 15 }, // Mutation Rate
    { wch: 18 }, // Guided Mutation
    { wch: 18 }, // Elite Preservation
    { wch: 24 }, // Similarity Diversification
    { wch: 24 }, // Adaptive Immigrant Refresh
    ...(includeSflaGaBaseline ? [{ wch: 18 }] : []), // SFLA-GA Baseline
  ];
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');

  // ==================== COMBINED WINNERS SHEET ====================
  const combinedWinnersHeader: (string | number)[] = [
    'Task Count', 'Perfect Winner', 'Perfect Winner Fitness', 'Perfect Winner Energy',
    'Best Alternative', 'Best Alt Fitness', 'Best Alt Energy', 'Best Alt Fitness Rank', 'Best Alt Energy Rank'
  ];
  const combinedWinnersData: (string | number)[][] = [
    ['Summary Basis', completeRunSummaryBasisLabel],
    [],
    combinedWinnersHeader,
  ];

  completeRunResults.forEach((entry) => {
    const combinedWinner = calculateCombinedWinner(entry.results);
    if (combinedWinner) {
      if (combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0) {
        combinedWinner.perfectWinners.forEach(winner => {
          combinedWinnersData.push([
            entry.taskCount,
            winner.name,
            winner.fitness,
            winner.energy,
            '-',
            '-',
            '-',
            winner.fitnessRank,
            winner.energyRank,
          ]);
        });
      } else if (combinedWinner.bestAlternative) {
        const alt = combinedWinner.bestAlternative;
        combinedWinnersData.push([
          entry.taskCount,
          '-',
          '-',
          '-',
          alt.name,
          alt.fitness,
          alt.energy,
          alt.fitnessRank,
          alt.energyRank,
        ]);
      } else {
        combinedWinnersData.push([
          entry.taskCount,
          'None',
          '-',
          '-',
          'None',
          '-',
          '-',
          '-',
          '-',
        ]);
      }
    } else {
      combinedWinnersData.push([
        entry.taskCount,
        'N/A',
        '-',
        '-',
        'N/A',
        '-',
        '-',
        '-',
        '-',
      ]);
    }
  });

  const combinedWinnersSheet = XLSX.utils.aoa_to_sheet(combinedWinnersData);
  combinedWinnersSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 18 }, // Perfect Winner
    { wch: 18 }, // Perfect Winner Fitness
    { wch: 18 }, // Perfect Winner Energy
    { wch: 18 }, // Best Alternative
    { wch: 18 }, // Best Alt Fitness
    { wch: 18 }, // Best Alt Energy
    { wch: 18 }, // Best Alt Fitness Rank
    { wch: 18 }, // Best Alt Energy Rank
  ];
  XLSX.utils.book_append_sheet(workbook, combinedWinnersSheet, 'Combined Winners');

  // ==================== COMPREHENSIVE RANKINGS SHEET ====================
  // Shows all rankings for all metrics at each task count
  const comprehensiveRankingsHeader: (string | number)[] = [
    'Task Count', 'Algorithm', 'Fitness Rank', 'Energy Rank', 'Cost Rank', 'Response Time Rank'
  ];
  const comprehensiveRankingsData: (string | number)[][] = [comprehensiveRankingsHeader];

  completeRunResults.forEach((entry) => {
    // Calculate rankings for each metric (using aggregated values when available)
    const fitnessRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'fitness') }))
      .sort((a, b) => b.value - a.value);
    const energyRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'energy') }))
      .sort((a, b) => a.value - b.value);
    const costRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'cost') }))
      .sort((a, b) => a.value - b.value);
    const responseTimeRanked = algorithmKeys.map(key => ({ key, value: getMetricValue(entry.results, key, 'responseTime') }))
      .sort((a, b) => a.value - b.value);

    algorithmKeys.forEach((key, idx) => {
      const fitnessRank = fitnessRanked.findIndex(r => r.key === key) + 1;
      const energyRank = energyRanked.findIndex(r => r.key === key) + 1;
      const costRank = costRanked.findIndex(r => r.key === key) + 1;
      const responseRank = responseTimeRanked.findIndex(r => r.key === key) + 1;

      comprehensiveRankingsData.push([
        entry.taskCount,
        algorithms[idx],
        fitnessRank,
        energyRank,
        costRank,
        responseRank,
      ]);
    });
  });

  const comprehensiveRankingsSheet = XLSX.utils.aoa_to_sheet(comprehensiveRankingsData);
  comprehensiveRankingsSheet['!cols'] = [
    { wch: 12 }, // Task Count
    { wch: 12 }, // Algorithm
    { wch: 14 }, // Fitness Rank
    { wch: 14 }, // Energy Rank
    { wch: 12 }, // Cost Rank
    { wch: 18 }, // Response Time Rank
  ];
  XLSX.utils.book_append_sheet(workbook, comprehensiveRankingsSheet, 'All Rankings');

  // ==================== QTI SUMMARY SHEETS ====================
  const qtiSummaryData: (string | number)[][] = [
    ['Derived Quality-Time Index (QTI) Summary'],
    ['Formula', 'QTI = Fitness / (1 + normalized execution time)'],
    ['Summary Basis', completeRunSummaryBasisLabel],
    ['Aggregation', 'Average QTI across exported task counts'],
    [''],
    ['Algorithm', 'Average QTI'],
  ];

  averageQTIRows.forEach((row) => {
    qtiSummaryData.push([row.algorithmName, formatSheetValue(row.averageQTI)]);
  });

  const qtiSummarySheet = XLSX.utils.aoa_to_sheet(qtiSummaryData);
  qtiSummarySheet['!cols'] = [
    { wch: 14 },
    { wch: 16 },
  ];
  XLSX.utils.book_append_sheet(workbook, qtiSummarySheet, 'QTI Summary');

  const qtiExceptionsData: (string | number)[][] = [
    ['QTI Exceptions Relative to SFLAGA'],
    ['Rule', 'Rows where competitor QTI > SFLAGA QTI for the same task count'],
    [''],
    ['Task Count', 'Algorithm', 'Algorithm QTI', 'SFLAGA QTI'],
  ];

  if (qtiExceptions.length === 0) {
    qtiExceptionsData.push(['None', 'No competitor exceeded SFLAGA on QTI', '', '']);
  } else {
    qtiExceptions.forEach((row) => {
      qtiExceptionsData.push([
        row.taskCount,
        row.algorithmName,
        formatSheetValue(row.qti),
        formatSheetValue(row.baselineQTI),
      ]);
    });
  }

  const qtiExceptionsSheet = XLSX.utils.aoa_to_sheet(qtiExceptionsData);
  qtiExceptionsSheet['!cols'] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
  ];
  XLSX.utils.book_append_sheet(workbook, qtiExceptionsSheet, 'QTI Exceptions');

  // ==================== FINDINGS & ANALYSIS SHEET ====================
  const findingsData: (string | number)[][] = [];
  
  // Header
  findingsData.push(['COMPLETE RUN FINDINGS & ANALYSIS']);
  findingsData.push([]);
  
  // Summary Information
  findingsData.push(['SUMMARY INFORMATION']);
  findingsData.push(['Total Task Configurations', completeRunResults.length]);
  findingsData.push(['Statistical Runs per Configuration', statisticalRunsPerConfiguration]);
  findingsData.push(['Start Date', new Date(completeRunResults[0].timestamp).toLocaleString()]);
  findingsData.push(['End Date', new Date(completeRunResults[completeRunResults.length - 1].timestamp).toLocaleString()]);
  findingsData.push(['Task Count Range', `${Math.min(...completeRunResults.map(r => r.taskCount))} - ${Math.max(...completeRunResults.map(r => r.taskCount))}`]);
  if (includeSflaGaBaseline) {
    findingsData.push(['SFLA-GA Baseline', 'Included']);
  }
  findingsData.push([]);
  
  // Best Performers Analysis
  findingsData.push(['BEST PERFORMERS ANALYSIS']);
  findingsData.push([]);
  
  // Count wins for each algorithm across all metrics and task counts
  const winCounts: { [key: string]: { fitness: number; energy: number; cost: number; responseTime: number; total: number } } = {};
  algorithms.forEach(alg => {
    winCounts[alg] = { fitness: 0, energy: 0, cost: 0, responseTime: 0, total: 0 };
  });

  completeRunResults.forEach((entry) => {
    // Fitness winners (higher is better) - using aggregated values
    const fitnessSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'fitness') }))
      .sort((a, b) => b.value - a.value);
    if (fitnessSorted.length > 0) winCounts[fitnessSorted[0].name].fitness++;
    
    // Energy winners (lower is better) - using aggregated values
    const energySorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'energy') }))
      .sort((a, b) => a.value - b.value);
    if (energySorted.length > 0) winCounts[energySorted[0].name].energy++;
    
    // Cost winners (lower is better) - using aggregated values
    const costSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'cost') }))
      .sort((a, b) => a.value - b.value);
    if (costSorted.length > 0) winCounts[costSorted[0].name].cost++;
    
    // Response Time winners (lower is better) - using aggregated values
    const responseTimeSorted = algorithmKeys.map(key => ({ key, name: algorithms[algorithmKeys.indexOf(key)], value: getMetricValue(entry.results, key, 'responseTime') }))
      .sort((a, b) => a.value - b.value);
    if (responseTimeSorted.length > 0) winCounts[responseTimeSorted[0].name].responseTime++;
  });

  // Calculate total wins
  algorithms.forEach(alg => {
    winCounts[alg].total = winCounts[alg].fitness + winCounts[alg].energy + winCounts[alg].cost + winCounts[alg].responseTime;
  });

  findingsData.push(['Algorithm', 'Fitness Wins', 'Energy Wins', 'Cost Wins', 'Response Time Wins', 'Total Wins']);
  algorithms.forEach(alg => {
    const counts = winCounts[alg];
    findingsData.push([alg, counts.fitness, counts.energy, counts.cost, counts.responseTime, counts.total]);
  });
  findingsData.push([]);

  // Combined Winner Analysis
  findingsData.push(['COMBINED WINNER ANALYSIS']);
  let perfectWinnerCount = 0;
  const bestAlternativeCounts: { [key: string]: number } = {};
  algorithms.forEach(alg => bestAlternativeCounts[alg] = 0);

  completeRunResults.forEach((entry) => {
    const combinedWinner = calculateCombinedWinner(entry.results);
    if (combinedWinner) {
      if (combinedWinner.hasPerfectWinner) {
        perfectWinnerCount += combinedWinner.perfectWinners.length;
      }
      if (combinedWinner.bestAlternative) {
        bestAlternativeCounts[combinedWinner.bestAlternative.name]++;
      }
    }
  });

  findingsData.push(['Perfect Winners (Total Occurrences)', perfectWinnerCount]);
  findingsData.push(['Best Alternative Counts:']);
  algorithms.forEach(alg => {
    if (bestAlternativeCounts[alg] > 0) {
      findingsData.push(['', `${alg}: ${bestAlternativeCounts[alg]}`]);
    }
  });
  findingsData.push([]);

  // Best Overall Algorithm
  findingsData.push(['BEST OVERALL ALGORITHM']);
  const sortedByTotalWins = algorithms.map(alg => ({ name: alg, total: winCounts[alg].total }))
    .sort((a, b) => b.total - a.total);
  if (sortedByTotalWins.length > 0) {
    findingsData.push(['Algorithm with Most Total Wins', sortedByTotalWins[0].name]);
    findingsData.push(['Total Wins', sortedByTotalWins[0].total]);
    findingsData.push([]);
    findingsData.push(['Overall Ranking by Total Wins:']);
    sortedByTotalWins.forEach((alg, idx) => {
      findingsData.push([`${idx + 1}.`, alg.name, `${alg.total} wins`]);
    });
  }
  findingsData.push([]);

  // Performance Trends
  findingsData.push(['PERFORMANCE TRENDS']);
  findingsData.push(['(See individual metric sheets for detailed trends)']);
  findingsData.push([]);

  // Key Findings Summary
  findingsData.push(['KEY FINDINGS']);
  findingsData.push(['1. Best Fitness Algorithm', sortedByTotalWins.length > 0 ? algorithms.reduce((best, alg) => winCounts[alg].fitness > winCounts[best].fitness ? alg : best) : 'N/A']);
  findingsData.push(['2. Best Energy Algorithm', algorithms.reduce((best, alg) => winCounts[alg].energy > winCounts[best].energy ? alg : best)]);
  findingsData.push(['3. Best Cost Algorithm', algorithms.reduce((best, alg) => winCounts[alg].cost > winCounts[best].cost ? alg : best)]);
  findingsData.push(['4. Best Response Time Algorithm', algorithms.reduce((best, alg) => winCounts[alg].responseTime > winCounts[best].responseTime ? alg : best)]);
  findingsData.push(['5. Most Consistent Performer', sortedByTotalWins.length > 0 ? sortedByTotalWins[0].name : 'N/A']);
  findingsData.push(['6. Perfect Winners Found', perfectWinnerCount]);
  findingsData.push([]);
  findingsData.push(['Note: Detailed metric values, rankings, solutions, and configurations are available in other sheets.']);

  const findingsSheet = XLSX.utils.aoa_to_sheet(findingsData);
  findingsSheet['!cols'] = [
    { wch: 30 }, // First column
    { wch: 20 }, // Second column
    { wch: 20 }, // Third column
    { wch: 20 }, // Fourth column
    { wch: 20 }, // Fifth column
    { wch: 15 }, // Sixth column
  ];
  XLSX.utils.book_append_sheet(workbook, findingsSheet, 'Findings & Analysis');

  // ==================== ENHANCED SUMMARY SHEET ====================
  const summaryData = [
    ['Complete Run Summary'],
    ['Total Task Configurations', completeRunResults.length],
    ['Statistical Runs per Configuration', statisticalRunsPerConfiguration],
    ['Start Date', new Date(completeRunResults[0].timestamp).toLocaleString()],
    ['End Date', new Date(completeRunResults[completeRunResults.length - 1].timestamp).toLocaleString()],
    [''],
    ['Summary Basis', completeRunSummaryBasisLabel],
    ['SFLAGA Enhancements', getSflagaEnhancementSummaryLine(completeRunResults[0].results)],
    ...(includeSflaGaBaseline ? [['SFLA-GA Baseline', 'Included']] : []),
    [''],
    ['Task Count Range'],
    ['Min', Math.min(...completeRunResults.map(r => r.taskCount))],
    ['Max', Math.max(...completeRunResults.map(r => r.taskCount))],
    [''],
    ['Task Counts'],
    ...completeRunResults.map(r => [r.taskCount]),
    [''],
    ['Sheet Overview'],
    ['1. Summary', 'Overview of the complete run'],
    ['2. Fitness', 'Fitness values across task counts'],
    ['3. Energy', 'Energy consumption across task counts'],
    ['4. Cost', 'Cost values across task counts'],
    ['5. Response Time', 'Response time across task counts'],
    ['6. Execution Time', 'Execution time across task counts'],
    ['7. QTI', 'Derived quality-overhead index across task counts'],
    ['8. Fitness Rankings', 'Rankings for fitness metric'],
    ['9. Energy Rankings', 'Rankings for energy metric'],
    ['10. Cost Rankings', 'Rankings for cost metric'],
    ['11. Response Rankings', 'Rankings for response time metric'],
    ['12. Detailed Results', 'All metrics for all algorithms, including QTI'],
    ['13. Solutions', 'Service selection solutions'],
    ['14. Configuration', 'Parameters used for each run'],
    ['15. Combined Winners', 'Perfect winners and best alternatives'],
    ['16. All Rankings', 'Comprehensive ranking matrix'],
    ['17. QTI Summary', 'Average derived QTI across task counts'],
    ['18. QTI Exceptions', 'Task counts where a competitor exceeds SFLAGA on QTI'],
    ['19. Findings & Analysis', 'Key findings and analysis'],
    ['20. Energy Box Plot', 'Box plot statistics for Energy (if available)'],
    ['21. Fitness Box Plot', 'Box plot statistics for Fitness (if available)'],
    ['22. Cost Box Plot', 'Box plot statistics for Cost (if available)'],
    ['23. Response Time Box Plot', 'Box plot statistics for Response Time (if available)'],
    [''],
    ['QTI Formula', 'QTI = Fitness / (1 + normalized execution time)'],
    ['Summary Basis Rule', completeRunSummaryBasisLabel],
    ['Note: Box plot sheets (20-23) are only included if numberOfRuns > 1'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [
    { wch: 25 },
    { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Add box plot sheets across all task counts when complete-run statistics are available.
  // This avoids collapsing the complete-run export down to a single task count.
  const energyBoxPlotSheet = createCompleteRunBoxPlotSheet(
    completeRunResults,
    algorithms,
    algorithmKeys,
    'energy'
  );
  if (energyBoxPlotSheet) {
    XLSX.utils.book_append_sheet(workbook, energyBoxPlotSheet, 'Energy Box Plot');
  }

  const fitnessBoxPlotSheet = createCompleteRunBoxPlotSheet(
    completeRunResults,
    algorithms,
    algorithmKeys,
    'fitness'
  );
  if (fitnessBoxPlotSheet) {
    XLSX.utils.book_append_sheet(workbook, fitnessBoxPlotSheet, 'Fitness Box Plot');
  }

  const costBoxPlotSheet = createCompleteRunBoxPlotSheet(
    completeRunResults,
    algorithms,
    algorithmKeys,
    'cost'
  );
  if (costBoxPlotSheet) {
    XLSX.utils.book_append_sheet(workbook, costBoxPlotSheet, 'Cost Box Plot');
  }

  const responseTimeBoxPlotSheet = createCompleteRunBoxPlotSheet(
    completeRunResults,
    algorithms,
    algorithmKeys,
    'responseTime'
  );
  if (responseTimeBoxPlotSheet) {
    XLSX.utils.book_append_sheet(workbook, responseTimeBoxPlotSheet, 'Response Time Box Plot');
  }

  // Write file
  XLSX.writeFile(workbook, filename);
}
